import { Component, type ErrorInfo, type ReactNode } from 'react'
import { reportClientError } from '../lib/telemetry'

type Props = { children: ReactNode }
type State = { crashed: boolean }

/**
 * Catches render-time crashes. Two jobs, both of which the app previously
 * lacked: report the crash so it stops being invisible, and show the user
 * something better than a blank page.
 */
export class ErrorBoundary extends Component<Props, State> {
  override state: State = { crashed: false }

  static getDerivedStateFromError(): State {
    return { crashed: true }
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    reportClientError({
      kind: 'render',
      message: error?.message ? String(error.message) : String(error),
      // The component stack localises the crash far better than the JS stack,
      // which is mostly React internals by the time it reaches us.
      stack: `${error?.stack ?? ''}\n--- component stack ---${info.componentStack ?? ''}`,
    })
  }

  override render(): ReactNode {
    if (!this.state.crashed) return this.props.children
    return (
      <div style={{ padding: 32, maxWidth: 520, margin: '10vh auto', textAlign: 'center' }}>
        <h1 style={{ fontSize: 20, marginBottom: 8 }}>Щось пішло не так</h1>
        <p style={{ color: 'var(--fg-2)', marginBottom: 20, lineHeight: 1.5 }}>
          Сторінку не вдалося показати. Ми вже отримали звіт про помилку — просто оновіть
          сторінку.
        </p>
        <button
          type="button"
          onClick={() => window.location.reload()}
          style={{
            border: '1px solid var(--border)',
            borderRadius: 4,
            padding: '8px 16px',
            background: 'var(--bg-1)',
            color: 'var(--fg-1)',
            cursor: 'pointer',
          }}
        >
          Оновити
        </button>
      </div>
    )
  }
}
